import React, { useCallback, useEffect, useRef, useState } from 'react'
import './PasswordConfirmation.scss'
import classNames from 'classnames'
import { SecondaryButton } from '@components/Buttons/SecondaryButton/SecondaryButton'
import DotSpinner from '@components/DotSpinner/DotSpinner'
import { TextTag } from '@enums/textTag'
import Text from '@components/Text/Text'
import { VelvetSDK } from 'sdk'
import { Params, useNavigate, useParams } from 'react-router-dom'
import {
  getEditProfileIdContentClassRoute,
  getEditProfileIdPinRoute,
  getEditProfileIdRoute,
} from '@constants/routes.const'
import { ShowPassword } from '@components/ShowPassword/ShowPassword'
import { KeyName, LocalStorageItem } from '@constants/index'
import { LoginInputField } from '@components/Login/LoginInputField/LoginInputField'
import { useTypedSelector } from 'store'
import { ParentalControlStep } from '@enums/profile.enum'
import { useDispatch } from 'react-redux'
import {
  setParentalAction,
  setParentalControl,
  setParentalStateSnapshot,
  setProfileContentClassification,
  setProfilePIN,
} from '@actions/profileActions'

interface PasswordConfirmationProps {
  showMenu: (status: boolean) => void
}

export const PasswordConfirmation = ({ showMenu }: PasswordConfirmationProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { profileId } = useParams<Params>()
  const inputRef = useRef<HTMLInputElement>(null)
  const [passwordValue, setPasswordValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const config = VelvetSDK.Configuration.configuration
  const apiEndpointBaseUrl = config.apiEndpointBaseUrl
  const userObject = JSON.parse(localStorage.getItem(LocalStorageItem.USER_DATA) as string)
  const userEmail = userObject?.email
  const showPasswordRef = useRef<HTMLFormElement>(null)
  const parentalActionLocal = useTypedSelector((state) => state.profile.parentalAction)
  const parentalStateSnapshot = useTypedSelector((state) => state.profile.parentalStateSnapshot)

  const handleCancelButton = useCallback(() => {
    // Restore the snapshot if it exists
    if (parentalStateSnapshot) {
      dispatch(setParentalControl(parentalStateSnapshot.parentalControl))
      dispatch(setProfileContentClassification(parentalStateSnapshot.contentClassification))
      dispatch(setProfilePIN(parentalStateSnapshot.pin))
    }
    // Clear the action and snapshot
    dispatch(setParentalAction(null))
    dispatch(setParentalStateSnapshot(null))

    navigate(getEditProfileIdRoute(profileId))
  }, [dispatch, navigate, parentalStateSnapshot, profileId])

  const login = useCallback(
    async (mail: string, pass: string) => {
      try {
        if (apiEndpointBaseUrl) {
          const response = await VelvetSDK.Authentication.service.login(mail, pass)
          setIsLoading(false)
          return response
        }
      } catch (error) {
        console.error('Error with login process', error)
        setIsLoading(false)
        return null
      }
    },
    [apiEndpointBaseUrl],
  )

  const navigateToShowPassword = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyName.ArrowRight) {
      if (inputRef.current?.value.length === inputRef.current?.selectionEnd) {
        inputRef.current?.blur()
        showPasswordRef.current?.focus()
      }
    }
  }, [])

  const handleContinueButton = useCallback(async () => {
    setIsLoading(true)
    const user = await login(userEmail, passwordValue)

    if (user) {
      setErrorMsg('')
      // Handle different parental control actions
      if (parentalActionLocal === ParentalControlStep.SET_PARENTAL_ON) {
        dispatch(setParentalControl(true))
        navigate(getEditProfileIdContentClassRoute(profileId))
      } else if (parentalActionLocal === ParentalControlStep.SET_PARENTAL_OFF) {
        dispatch(setParentalControl(false))
        // Clear the local values when turning OFF
        dispatch(setProfileContentClassification(''))
        dispatch(setProfilePIN(''))
        navigate(getEditProfileIdRoute(profileId))
      } else if (parentalActionLocal === ParentalControlStep.CHANGE_CONTENT) {
        navigate(getEditProfileIdContentClassRoute(profileId))
      } else if (parentalActionLocal === ParentalControlStep.CHANGE_PIN) {
        navigate(getEditProfileIdPinRoute(profileId))
      }
    } else {
      setErrorMsg(V.profileIncorrectPassword_TV)
      setIsLoading(false)
    }
  }, [
    V.profileIncorrectPassword_TV,
    dispatch,
    login,
    navigate,
    parentalActionLocal,
    passwordValue,
    profileId,
    userEmail,
  ])

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordValue(e.target.value)
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    handleContinueButton()
  }

  const getPasswordSubtitle = () => {
    if (parentalActionLocal === ParentalControlStep.SET_PARENTAL_ON) {
      return V.parentalControlPasswordRequiredInfo
    } else if (parentalActionLocal === ParentalControlStep.SET_PARENTAL_OFF) {
      return V.disableParentalControlInfo
    } else {
      return V.confirmYourPasswordToEdit
    }
  }

  useEffect(() => {
    showMenu(false)
    inputRef.current && inputRef.current.focus()
  }, [showMenu])

  return (
    <div className="password-confirmation-page">
      <div className={classNames('password-confirm-wrapper', isLoading && 'is-loading')}>
        <Text tag={TextTag.H1}>{V.parentalControlPasswordRequired}</Text>
        <Text tag={TextTag.P}>{getPasswordSubtitle()}</Text>
        <div className="input-wrapper">
          <div className="password-input-wrapper">
            <LoginInputField
              type={showPassword ? 'text' : 'password'}
              label=""
              inputValue={passwordValue}
              errorMessage={errorMsg}
              onChange={handlePasswordChange}
              onSubmit={handleSubmit}
              placeholder={V.profileEnterPasswordLabel}
              inputRef={inputRef}
              navigateToShowPassword={navigateToShowPassword}
            />
            <ShowPassword setShowPassword={setShowPassword} showPassword={showPassword} ref={showPasswordRef} />
          </div>
        </div>
        <div className="buttons">
          <SecondaryButton disabled={passwordValue.trim().length === 0} onClick={handleContinueButton}>
            {V.continueButtonLabel}
          </SecondaryButton>
          <SecondaryButton onClick={handleCancelButton}>{V.genericCancelLabel}</SecondaryButton>
        </div>
      </div>
      {isLoading && <DotSpinner />}
    </div>
  )
}
